import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Base_URL } from '../../url';
import '../../Styling/PersonalDashboard.css';
import { useAuth } from '../../Context/AuthContext'; 


const PersonalDashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    totalIncome: 0,
    totalInvestment: 0,
    debtReceived: 0,
    debtCleared: 0,
    totalReceivable: 0,
    totalPayable: 0,
    totalExpense: 0,
    totalExpenseDocumentCount: 0
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const [lastRefreshed, setLastRefreshed] = useState('');
  const [expenseDetails, setExpenseDetails] = useState([]);
  const [showExpenseDetails, setShowExpenseDetails] = useState(false);
   const { user} =  useAuth();

  const fetchDashboardData = async () => {
  try {
    setLoading(true);
    setError(null);

    const response = await axios.get(
      `${Base_URL}/personalDashboard/GetPersonalDashboardData`,
      {
        params: {
          org_id: user?.organisationId,  
          period: selectedPeriod        
        },
        headers: {
          Authorization: `Bearer ${user?.token}`, 
          'Content-Type': 'application/json'
        }
      }
    );

    console.log("Full API Response:", response);
    console.log("Response Data:", response.data);

    if (response.data) {
      const data = response.data;

      // Update Dashboard
      setDashboardData(prev => ({
        ...prev,
        totalExpense: data?.summary?.totalExpenseAmount || 0,
        totalExpenseDocumentCount: data?.summary?.totalDocumentCountExpense || 0
      }));

      setExpenseDetails(data?.details || []);
    } else {
      throw new Error("No data received from server");
    }

    setLastRefreshed(new Date().toLocaleTimeString());
    
  } catch (err) {
    console.error("API Error:", err);

    const errorMsg =
      err.response?.data?.message ||
      err.response?.data?.error ||
      err.message ||
      "Unable to fetch dashboard data";

    setError(errorMsg);

    // Reset values on error
    setDashboardData({
      totalIncome: 0,
      totalInvestment: 0,
      debtReceived: 0,
      debtCleared: 0,
      totalReceivable: 0,
      totalPayable: 0,
      totalExpense: 0,
      totalExpenseDocumentCount: 0
    });
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    fetchDashboardData();
  }, [selectedPeriod]);

  const formatInLacs = (amount) => {
    const inLacs = (amount / 100000).toFixed(2);
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(inLacs);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Get color for category
  const getCategoryColor = (category) => {
    const colors = {
      'MISC': '#FF6B6B',
      'PARTY': '#4ECDC4',
      'TRAVEL': '#FFD166',
      'FOOD': '#06D6A0',
      'SHOPPING': '#118AB2',
      'BILLS': '#EF476F',
      'HEALTH': '#7209B7',
      'EDUCATION': '#F3722C'
    };
    return colors[category] || '#6C757D';
  };

  // Loading State
  if (loading) {
    return (
      <div className="personal-dashboard-loading">
        <div className="personal-dashboard-spinner"></div>
        <p>Loading your dashboard...</p>
        <p className="personal-dashboard-loading-sub">Fetching data from server</p>
      </div>
    );
  }

  return (
    <div className="personal-dashboard-container">
      {/* Header with Refresh Button */}
      <div className="personal-dashboard-header">
        <div className="personal-dashboard-header-left">
          <h1>Personal Dashboard</h1>
          {lastRefreshed && (
            <p className="last-refreshed">Last updated: {lastRefreshed}</p>
          )}
        </div>
      
      </div>

      {/* Financial Cards Grid - ONLY 7 CARDS */}
      <div className="personal-dashboard-grid">
        {/* Income Card */}
        <div className="personal-dashboard-card income-card">
          <div className="personal-dashboard-card-header">
            <div className="personal-dashboard-card-icon">💰</div>
            <h3>Total Income</h3>
          </div>
          <div className="personal-dashboard-card-body">
            <div className="personal-dashboard-card-amount">
              ₹{formatInLacs(dashboardData.totalIncome)} Lacs
            </div>
            <p className="personal-dashboard-card-desc">Net income received</p>
          </div>
        </div>

        {/* Investment Card */}
        <div className="personal-dashboard-card investment-card">
          <div className="personal-dashboard-card-header">
            <div className="personal-dashboard-card-icon">📈</div>
            <h3>Total Investment</h3>
          </div>
          <div className="personal-dashboard-card-body">
            <div className="personal-dashboard-card-amount">
              ₹{formatInLacs(dashboardData.totalInvestment)} Lacs
            </div>
            <p className="personal-dashboard-card-desc">Invested amount</p>
          </div>
        </div>

        {/* Debt Received Card */}
        <div className="personal-dashboard-card debt-card">
          <div className="personal-dashboard-card-header">
            <div className="personal-dashboard-card-icon">📥</div>
            <h3>Debt Received</h3>
          </div>
          <div className="personal-dashboard-card-body">
            <div className="personal-dashboard-card-amount">
              ₹{formatInLacs(dashboardData.debtReceived)} Lacs
            </div>
            <p className="personal-dashboard-card-desc">Loans received</p>
          </div>
        </div>

        {/* Debt Cleared Card */}
        <div className="personal-dashboard-card debt-clear-card">
          <div className="personal-dashboard-card-header">
            <div className="personal-dashboard-card-icon">✅</div>
            <h3>Debt Cleared</h3>
          </div>
          <div className="personal-dashboard-card-body">
            <div className="personal-dashboard-card-amount">
              ₹{formatInLacs(dashboardData.debtCleared)} Lacs
            </div>
            <p className="personal-dashboard-card-desc">Loans repaid</p>
          </div>
        </div>

        {/* Receivable Card */}
        <div className="personal-dashboard-card receivable-card">
          <div className="personal-dashboard-card-header">
            <div className="personal-dashboard-card-icon">📥</div>
            <h3>Total Receivable</h3>
          </div>
          <div className="personal-dashboard-card-body">
            <div className="personal-dashboard-card-amount">
              ₹{formatInLacs(dashboardData.totalReceivable)} Lacs
            </div>
            <p className="personal-dashboard-card-desc">To be received</p>
          </div>
        </div>

        {/* Payable Card */}
        <div className="personal-dashboard-card payable-card">
          <div className="personal-dashboard-card-header">
            <div className="personal-dashboard-card-icon">📤</div>
            <h3>Total Payable</h3>
          </div>
          <div className="personal-dashboard-card-body">
            <div className="personal-dashboard-card-amount">
              ₹{formatInLacs(dashboardData.totalPayable)} Lacs
            </div>
            <p className="personal-dashboard-card-desc">To be paid</p>
          </div>
        </div>

        {/* Expense Card with Document Count - LAST CARD IN GRID */}
        <div className="personal-dashboard-card expense-card">
          <div className="personal-dashboard-card-header">
            <div className="personal-dashboard-card-icon">💸</div>
            <h3>Total Expense</h3>
          </div>
          <div className="personal-dashboard-card-body">
            <div className="personal-dashboard-card-amount">
              ₹{formatInLacs(dashboardData.totalExpense)} Lacs
            </div>  
            {/* Document Count */}
            <div className="document-count">
              <span className="document-count-label">Documents:</span>
              <span className="document-count-value">{dashboardData.totalExpenseDocumentCount}</span>
            </div>
            <p className="personal-dashboard-card-desc">Total expenses</p>
          </div>
        </div>
      </div>
   

      {/* ===== Expense Breakdown Section - BELOW THE GRID ===== */}
      <div className="expense-breakdown-section">
        <div className="expense-breakdown-card">
          <div 
            className="expense-breakdown-header"
            onClick={() => setShowExpenseDetails(!showExpenseDetails)}
          >
            <div className="header-left">
              <div className="header-icon">📊</div>
              <div>
                <h3>Expense Breakdown</h3>
                <p className="breakdown-subtitle">Detailed view of expense categories</p>
              </div>
            </div>
            <div className="header-right">
              <div className="total-amount-display">
                <span className="total-label">Total:</span>
                <span className="total-amount">₹{formatInLacs(dashboardData.totalExpense)} Lacs</span>
              </div>
              <div className={`expand-icon ${showExpenseDetails ? 'expanded' : ''}`}>
                ▼
              </div>
            </div>
          </div>
          
          {showExpenseDetails && (
            <div className="expense-breakdown-content">
              {/* Summary Stats */}
              <div className="expense-summary-stats">
                <div className="stat-item">
                  <div className="stat-label">Categories</div>
                  <div className="stat-value">{expenseDetails.length}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Total Documents</div>
                  <div className="stat-value">{dashboardData.totalExpenseDocumentCount}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Period</div>
                  <div className="stat-value">
                    {selectedPeriod.charAt(0).toUpperCase() + selectedPeriod.slice(1)}
                  </div>
                </div>
              </div>

              {/* Expense Categories List */}
              <div className="expense-categories-list">
                {expenseDetails.length > 0 ? (
                  expenseDetails.map((item, index) => {
                    const percentage = dashboardData.totalExpense > 0 
                      ? ((item.totalAmount / dashboardData.totalExpense) * 100).toFixed(1)
                      : 0;
                    
                    return (
                      <div key={index} className="expense-category-item">
                        <div className="category-info">
                          <div 
                            className="category-color-indicator"
                            style={{ backgroundColor: getCategoryColor(item.head3) }}
                          />
                          <div className="category-details">
                            <div className="category-name">{item.head3}</div>
                            <div className="category-meta">
                              <span className="document-count-badge">
                                📄 {item.documentCount} documents
                              </span>
                              {item.organisationId && (
                                <span className="org-badge">Org: {item.organisationId}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="category-amounts">
                          <div className="amount-display">
                            <span className="amount-value">
                              {formatCurrency(item.totalAmount)}
                            </span>
                            <span className="amount-percentage">
                              {percentage}%
                            </span>
                          </div>
                          <div className="progress-bar-container">
                            <div 
                              className="progress-bar"
                              style={{ 
                                width: `${percentage}%`,
                                backgroundColor: getCategoryColor(item.head3)
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="no-data-message">
                    <div className="no-data-icon">📄</div>
                    <p>No expense data available for the selected period</p>
                  </div>
                )}
              </div>

              {/* Category Legend */}
              {expenseDetails.length > 0 && (
                <div className="category-legend">
                  <h4>Category Legend</h4>
                  <div className="legend-items">
                    {expenseDetails.map((item, index) => (
                      <div key={index} className="legend-item">
                        <div 
                          className="legend-color"
                          style={{ backgroundColor: getCategoryColor(item.head3) }}
                        />
                        <span className="legend-label">{item.head3}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="personal-dashboard-footer">
        <div className="personal-dashboard-footer-left">
          <div className="personal-dashboard-legend">
            <div className="personal-dashboard-legend-item">
              <div className="personal-dashboard-legend-color income"></div>
              <span>Income</span>
            </div>
            <div className="personal-dashboard-legend-item">
              <div className="personal-dashboard-legend-color investment"></div>
              <span>Investment</span>
            </div>
            <div className="personal-dashboard-legend-item">
              <div className="personal-dashboard-legend-color expense"></div>
              <span>Expense</span>
            </div>
            <div className="personal-dashboard-legend-item">
              <div className="personal-dashboard-legend-color debt"></div>
              <span>Debt</span>
            </div>
          </div>
        </div>
        <div className="personal-dashboard-footer-right">
          <p className="personal-dashboard-currency-note">
            All amounts displayed in Lacs (₹100,000)
          </p>
        </div>
      </div>
    </div>
  );
};

export default PersonalDashboard;